/** @jsxRuntime automatic */
import { useEffect, useId, type ReactElement, type ReactNode, cloneElement, isValidElement, type HTMLAttributes } from "react";
import { MOTION } from "./motion";
import { cn } from "./utils";

export type TooltipSide = "top" | "bottom" | "right";
export type TooltipAlign = "center" | "start" | "end";

/*
 * When a tooltip shows (components-09): hover with a fine pointer, or focus
 * that came from the keyboard (`:focus-visible`). A tap focuses a button on
 * Android without matching `:focus-visible`, so nothing stays stuck on a
 * phone after a touch. Hidden is `display: none`: a bubble at the screen edge
 * never adds sideways scroll. How it shows is CSS (`.ui-tooltip` in
 * globals.css, spec 07 › Flotantes): after 300 ms of hover, at once with
 * keyboard focus or in a warm group, opacity and .97 → 1 in 80, out by
 * opacity in 80; these classes only switch its `display`. Keyboard focus on
 * the control a `Tooltip` wraps is a sibling rule there
 * (`.group/tooltip > :focus-visible ~ .ui-tooltip`), never a :has(): a
 * `:has(:focus-visible)` names no class, attribute or tag, so Chromium
 * re-tested it on every insertion in the page (7–10 ms of style per commit at
 * 4x on the Assistant), and Tailwind's `group-has-*`
 * (`:is(:where(.group):has(…) *)`) restyled the whole page.
 */
const SHOW = "pointer-fine:group-hover/tooltip:flex group-focus-visible/tooltip:flex";
/** Escape hides it until the next hover or focus (WCAG 1.4.13, a11y-20). */
const DISMISSED = "group-data-[tooltip-dismissed]/tooltip:hidden!";

const TOOLTIP_GROUP_SELECTOR = ".group\\/tooltip";
let listenersInstalled = false;

function isHovered(element: Element): boolean {
  try {
    return element.matches(":hover");
  } catch {
    return false;
  }
}

/**
 * Clears the dismissal once the pointer and the focus have both left the
 * group (Escape), or once the pointer has (a press: the button keeps the
 * focus after its click, and the next hover shows its name again).
 */
function rearmOnLeave(group: HTMLElement, { pointerOnly = false } = {}) {
  const clear = () => {
    if ((!pointerOnly && group.contains(document.activeElement)) || isHovered(group)) return;
    delete group.dataset.tooltipDismissed;
    group.removeEventListener("pointerleave", clear);
    group.removeEventListener("focusout", onFocusOut);
  };
  const onFocusOut = (event: FocusEvent) => {
    if (event.relatedTarget instanceof Node && group.contains(event.relatedTarget)) return;
    // `activeElement` still points inside during focusout: check after it moves.
    queueMicrotask(clear);
  };
  group.addEventListener("pointerleave", clear);
  group.addEventListener("focusout", onFocusOut);
}

/** The last key pressed (null after a press of the pointer): a focus that follows Tab or an arrow is the keyboard's navigation. */
let lastKey: string | null = null;
const NAVIGATION_KEYS = new Set(["Tab", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown", "F6"]);

let hovered: Element | null = null;
let warmTimer = 0;
let coolTimer = 0;

/**
 * The warm group (spec 07 › Flotantes): while a tooltip shows and for 300 ms
 * after the pointer leaves it, the next one shows at once (running the pointer
 * along a toolbar reads every name), on `<html data-tooltip-warm>`. Set as the
 * first tooltip shows, not as the pointer reaches the next one: the browser
 * styles the new hover before any listener of that move runs.
 */
function trackWarmth(group: Element | null) {
  if (group === hovered) return;
  hovered = group;
  const root = document.documentElement;
  window.clearTimeout(warmTimer);
  if (group) {
    window.clearTimeout(coolTimer);
    if (root.dataset.tooltipWarm === undefined) {
      warmTimer = window.setTimeout(() => (root.dataset.tooltipWarm = ""), MOTION.delay.tooltip);
    }
  } else if (root.dataset.tooltipWarm !== undefined) {
    coolTimer = window.setTimeout(() => delete root.dataset.tooltipWarm, MOTION.delay.tooltipWarm);
  }
}

/**
 * One set of document listeners for every tooltip. Escape dismisses the
 * tooltips that are showing (hovered or holding the focus) without moving
 * the pointer or the focus; it never stops the key: inside a sheet the same
 * Escape also closes the sheet, which takes the tooltip with it. A press
 * with a fine pointer dismisses its group's tooltip (the name is read; the
 * action is what matters now), unless the group keeps it
 * (`data-tooltip-keep`: its text is the press's answer, «Copiar» →
 * «Copiado») or the press lands on a hoverable bubble (its text is being
 * selected). The pointer moving over the groups keeps the warm group.
 */
function installTooltipListeners() {
  if (listenersInstalled || typeof document === "undefined") return;
  listenersInstalled = true;
  document.addEventListener(
    "keydown",
    (event) => {
      lastKey = event.key;
      if (event.key !== "Escape") return;
      for (const group of Array.from(document.querySelectorAll<HTMLElement>(TOOLTIP_GROUP_SELECTOR))) {
        if (group.dataset.tooltipDismissed !== undefined) continue;
        if (!group.contains(document.activeElement) && !isHovered(group)) continue;
        group.dataset.tooltipDismissed = "";
        rearmOnLeave(group);
      }
    },
    true,
  );
  document.addEventListener(
    "pointerdown",
    (event) => {
      if (event.pointerType === "touch") return;
      const target = event.target as Element | null;
      if (target?.closest?.(".ui-tooltip")) return;
      const group = target?.closest?.<HTMLElement>(TOOLTIP_GROUP_SELECTOR);
      if (!group || group.dataset.tooltipDismissed !== undefined || group.dataset.tooltipKeep !== undefined) return;
      group.dataset.tooltipDismissed = "";
      rearmOnLeave(group, { pointerOnly: true });
    },
    { capture: true, passive: true },
  );
  // Only the keyboard's own navigation names a control as it takes the
  // focus: a focus the app gives back after another key (Escape closed the
  // list's search, a menu, a sheet) lands quiet, with its ring and without
  // its bubble, until the pointer and the focus leave it.
  document.addEventListener(
    "focusin",
    (event) => {
      if (lastKey === null || NAVIGATION_KEYS.has(lastKey)) return;
      const group = (event.target as Element | null)?.closest?.<HTMLElement>(TOOLTIP_GROUP_SELECTOR);
      if (!group || group.dataset.tooltipDismissed !== undefined) return;
      group.dataset.tooltipDismissed = "";
      rearmOnLeave(group);
    },
    true,
  );
  document.addEventListener(
    "pointerdown",
    () => {
      lastKey = null;
    },
    { capture: true, passive: true },
  );
  document.addEventListener(
    "pointerover",
    (event) => {
      if (event.pointerType === "touch") return;
      trackWarmth((event.target as Element | null)?.closest?.(TOOLTIP_GROUP_SELECTOR) ?? null);
    },
    { capture: true, passive: true },
  );
}

/** Where it sits, and the edge it grows from (its trigger's). */
const SIDE: Record<TooltipSide, string> = {
  top: "bottom-full mb-1.5 origin-bottom",
  bottom: "top-full mt-1.5 origin-top",
  right: "left-full top-1/2 ml-2 -translate-y-1/2 origin-left",
};

const ALIGN: Record<TooltipAlign, string> = {
  center: "left-1/2 -translate-x-1/2",
  start: "left-0",
  end: "right-0",
};

/*
 * A hoverable bubble (WCAG 1.4.13): the pointer can move from the trigger onto
 * the text without it disappearing; a transparent 8 px bridge covers the gap.
 * Only for `Tooltip`, whose bubble sits beside its trigger: inside an
 * IconButton the bubble is part of the button, so a click on it would press it.
 */
const BRIDGE: Record<TooltipSide, string> = {
  top: "before:-bottom-2 before:inset-x-0 before:h-2",
  bottom: "before:-top-2 before:inset-x-0 before:h-2",
  right: "before:-left-2 before:inset-y-0 before:w-2",
};

/** Classes of the bubble, for an element that is its own `group/tooltip`. */
export function tooltipBubbleClass(side: TooltipSide = "top", align: TooltipAlign = "center", hoverable = false): string {
  return cn(
    "ui-tooltip absolute z-popover hidden w-max max-w-64 items-center gap-2 whitespace-normal rounded-md bg-foreground px-2 py-1 text-left text-caption font-medium normal-case tracking-normal text-background shadow-popover",
    hoverable ? cn("pointer-events-auto before:absolute before:content-['']", BRIDGE[side]) : "pointer-events-none",
    SIDE[side],
    side === "right" ? null : ALIGN[align],
    SHOW,
    DISMISSED,
  );
}

export interface TooltipBubbleProps {
  children: ReactNode;
  side?: TooltipSide;
  align?: TooltipAlign;
  /** Keyboard shortcut shown after the label (["G", "I"]). */
  keys?: readonly string[];
  /**
   * `true` (default) when the bubble repeats the control's accessible name
   * (IconButton, rail links): hidden from screen readers so it is not read twice.
   */
  decorative?: boolean;
  /** The pointer can rest on the bubble (only when it sits outside its control). */
  hoverable?: boolean;
  id?: string;
  className?: string;
}

/**
 * The bubble inside a control that carries `group/tooltip` (and `relative`).
 * IconButton and the sidebar rail use it; for any other element wrap it in
 * `Tooltip`. One tooltip style for the whole product.
 */
export function TooltipBubble({ children, side = "top", align = "center", keys, decorative = true, hoverable = false, id, className }: TooltipBubbleProps) {
  useEffect(installTooltipListeners, []);
  return (
    <span
      id={id}
      role={decorative ? undefined : "tooltip"}
      aria-hidden={decorative ? true : undefined}
      className={cn(tooltipBubbleClass(side, align, hoverable), className)}
    >
      {children}
      {keys && keys.length > 0 ? (
        <span className="flex gap-0.5 opacity-70">
          {keys.map((key) => (
            <kbd key={key} className="font-sans">
              {key}
            </kbd>
          ))}
        </span>
      ) : null}
    </span>
  );
}

export interface TooltipProps {
  /** Short text (a time, an absolute date, an icon's name). */
  content: string;
  /** One focusable child (IconButton, time element…). */
  children: ReactElement<HTMLAttributes<HTMLElement>>;
  side?: "top" | "bottom";
  align?: TooltipAlign;
  /**
   * The bubble stays through a press (`data-tooltip-keep`): its `content` is
   * the press's answer («Copiar» → «Copiado», spec 07 › Movimiento).
   */
  keepOnPress?: boolean;
  className?: string;
}

/**
 * Small hover/keyboard-focus label that replaces the native `title` (spec 07
 * › Tooltip). Shown only with a fine pointer or on keyboard focus; touch gets
 * the accessible name instead. CSS shows it (no portal, no timers); the pointer
 * can rest on it, Escape dismisses it until the next hover or focus and a
 * press until the pointer leaves (`keepOnPress` keeps it). Its text is the
 * control's description, after any it already had; when it only repeats the
 * control's name, it is for the eye alone. An icon-only control is an
 * `IconButton`, whose bubble is its name.
 */
export function Tooltip({ content, children, side = "top", align = "center", keepOnPress = false, className }: TooltipProps) {
  const id = useId();
  const child = isValidElement(children) ? children : null;
  // A bubble that only repeats the control's name (its `aria-label`, or its
  // text) is no description: it is read once, as the name, and the bubble is
  // only for the eye (q-ui-package-09). Otherwise it joins whatever the
  // control is already described by (an error, a hint), never over it.
  const repeatsName = child != null && namesItself(child.props, content);
  const describedBy = child?.props["aria-describedby"];
  const described = child && !repeatsName
    ? cloneElement(child, { "aria-describedby": describedBy ? `${describedBy} ${id}` : id } as HTMLAttributes<HTMLElement>)
    : children;
  return (
    <span className={cn("group/tooltip relative inline-flex", className)} data-tooltip-keep={keepOnPress ? "" : undefined}>
      {described}
      <TooltipBubble id={repeatsName ? undefined : id} side={side} align={align} decorative={repeatsName} hoverable>
        {content}
      </TooltipBubble>
    </span>
  );
}

/** The control's accessible name is already `content`: its `aria-label`, or its own plain text. */
function namesItself(props: HTMLAttributes<HTMLElement>, content: string): boolean {
  const name = props["aria-label"] ?? (typeof props.children === "string" ? props.children : null);
  return name != null && name.trim() === content.trim();
}
